/**
 * Vector point editing utilities for the canvas editor.
 * Parses SVG path strings into editable VectorPoint arrays,
 * serializes them back, and provides hit-testing for point handles.
 */

import type { Point } from './canvaskit-hit-test';

// ─── Types ────────────────────────────────────────────────────────────────

export interface VectorPoint {
  x: number;
  y: number;
  /** Incoming bezier handle, relative to anchor. null = no handle (straight segment). */
  handleIn: { x: number; y: number } | null;
  /** Outgoing bezier handle, relative to anchor. null = no handle (straight segment). */
  handleOut: { x: number; y: number } | null;
  cornerType: 'corner' | 'smooth' | 'symmetric';
  /** Per-point corner radius from Figma vectorNetwork. */
  cornerRadius?: number;
}

export interface VectorPath {
  points: VectorPoint[];
  closed: boolean;
  /** Fill rule for this subpath. Defaults to 'nonzero' if omitted. */
  windingRule?: 'evenodd' | 'nonzero';
}

export interface VectorPointHit {
  pathIndex: number;
  pointIndex: number;
  part: 'anchor' | 'handleIn' | 'handleOut';
}

// ─── SVG Path Parsing ─────────────────────────────────────────────────────

/**
 * Parse an SVG path data string into VectorPath arrays.
 * Handles M, L, H, V, C, S, Q, T, Z commands (absolute and relative).
 * Arcs (A) are approximated as line-to-endpoint.
 */
export function parseSvgPathToVectorPaths(pathData: string): VectorPath[] {
  const paths: VectorPath[] = [];
  let currentPoints: VectorPoint[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;
  let lastCp2X = 0; // for S/T smooth continuation
  let lastCp2Y = 0;
  let lastCmd = '';

  function addPoint(x: number, y: number, handleIn: VectorPoint['handleIn'] = null, handleOut: VectorPoint['handleOut'] = null) {
    currentPoints.push({ x, y, handleIn, handleOut, cornerType: handleIn || handleOut ? 'smooth' : 'corner' });
  }

  function startSubpath(x: number, y: number) {
    if (currentPoints.length > 0) {
      paths.push({ points: currentPoints, closed: false });
    }
    currentPoints = [];
    addPoint(x, y);
    startX = x;
    startY = y;
  }

  const tokens = pathData.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!tokens) return paths;

  for (const token of tokens) {
    const cmd = token[0];
    const nums = token.slice(1).trim().match(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/gi)?.map(Number) ?? [];

    switch (cmd) {
      case 'M':
        for (let i = 0; i < nums.length; i += 2) {
          if (i === 0) {
            startSubpath(nums[i], nums[i + 1]);
          } else {
            // Implicit line-to after first M pair
            addPoint(nums[i], nums[i + 1]);
          }
          cx = nums[i];
          cy = nums[i + 1];
        }
        break;
      case 'm':
        for (let i = 0; i < nums.length; i += 2) {
          const nx = cx + nums[i];
          const ny = cy + nums[i + 1];
          if (i === 0) {
            startSubpath(nx, ny);
          } else {
            addPoint(nx, ny);
          }
          cx = nx;
          cy = ny;
        }
        break;

      case 'L':
        for (let i = 0; i < nums.length; i += 2) {
          cx = nums[i]; cy = nums[i + 1];
          addPoint(cx, cy);
        }
        break;
      case 'l':
        for (let i = 0; i < nums.length; i += 2) {
          cx += nums[i]; cy += nums[i + 1];
          addPoint(cx, cy);
        }
        break;

      case 'H':
        for (const n of nums) { cx = n; addPoint(cx, cy); }
        break;
      case 'h':
        for (const n of nums) { cx += n; addPoint(cx, cy); }
        break;

      case 'V':
        for (const n of nums) { cy = n; addPoint(cx, cy); }
        break;
      case 'v':
        for (const n of nums) { cy += n; addPoint(cx, cy); }
        break;

      case 'C':
        for (let i = 0; i < nums.length; i += 6) {
          const cp1x = nums[i], cp1y = nums[i + 1];
          const cp2x = nums[i + 2], cp2y = nums[i + 3];
          const ex = nums[i + 4], ey = nums[i + 5];
          // Set handleOut on previous point
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          // Add new point with handleIn
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = cp2x; lastCp2Y = cp2y;
          cx = ex; cy = ey;
        }
        break;
      case 'c':
        for (let i = 0; i < nums.length; i += 6) {
          const cp1x = cx + nums[i], cp1y = cy + nums[i + 1];
          const cp2x = cx + nums[i + 2], cp2y = cy + nums[i + 3];
          const ex = cx + nums[i + 4], ey = cy + nums[i + 5];
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = cp2x; lastCp2Y = cp2y;
          cx = ex; cy = ey;
        }
        break;

      case 'S':
        for (let i = 0; i < nums.length; i += 4) {
          // Reflected control point from previous C/S
          const cp1x = (lastCmd === 'C' || lastCmd === 'c' || lastCmd === 'S' || lastCmd === 's')
            ? 2 * cx - lastCp2X : cx;
          const cp1y = (lastCmd === 'C' || lastCmd === 'c' || lastCmd === 'S' || lastCmd === 's')
            ? 2 * cy - lastCp2Y : cy;
          const cp2x = nums[i], cp2y = nums[i + 1];
          const ex = nums[i + 2], ey = nums[i + 3];
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = cp2x; lastCp2Y = cp2y;
          cx = ex; cy = ey;
        }
        break;
      case 's':
        for (let i = 0; i < nums.length; i += 4) {
          const cp1x = (lastCmd === 'C' || lastCmd === 'c' || lastCmd === 'S' || lastCmd === 's')
            ? 2 * cx - lastCp2X : cx;
          const cp1y = (lastCmd === 'C' || lastCmd === 'c' || lastCmd === 'S' || lastCmd === 's')
            ? 2 * cy - lastCp2Y : cy;
          const cp2x = cx + nums[i], cp2y = cy + nums[i + 1];
          const ex = cx + nums[i + 2], ey = cy + nums[i + 3];
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = cp2x; lastCp2Y = cp2y;
          cx = ex; cy = ey;
        }
        break;

      case 'Q':
        for (let i = 0; i < nums.length; i += 4) {
          const qx = nums[i], qy = nums[i + 1];
          const ex = nums[i + 2], ey = nums[i + 3];
          // Convert quadratic to cubic handles (degree elevation)
          const cp1x = cx + (2 / 3) * (qx - cx);
          const cp1y = cy + (2 / 3) * (qy - cy);
          const cp2x = ex + (2 / 3) * (qx - ex);
          const cp2y = ey + (2 / 3) * (qy - ey);
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = qx; lastCp2Y = qy;
          cx = ex; cy = ey;
        }
        break;
      case 'q':
        for (let i = 0; i < nums.length; i += 4) {
          const qx = cx + nums[i], qy = cy + nums[i + 1];
          const ex = cx + nums[i + 2], ey = cy + nums[i + 3];
          const cp1x = cx + (2 / 3) * (qx - cx);
          const cp1y = cy + (2 / 3) * (qy - cy);
          const cp2x = ex + (2 / 3) * (qx - ex);
          const cp2y = ey + (2 / 3) * (qx - ey);
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
            if (prev.handleOut.x !== 0 || prev.handleOut.y !== 0) prev.cornerType = 'smooth';
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = qx; lastCp2Y = qy;
          cx = ex; cy = ey;
        }
        break;

      case 'T':
        for (let i = 0; i < nums.length; i += 2) {
          const qx = (lastCmd === 'Q' || lastCmd === 'q' || lastCmd === 'T' || lastCmd === 't')
            ? 2 * cx - lastCp2X : cx;
          const qy = (lastCmd === 'Q' || lastCmd === 'q' || lastCmd === 'T' || lastCmd === 't')
            ? 2 * cy - lastCp2Y : cy;
          const ex = nums[i], ey = nums[i + 1];
          const cp1x = cx + (2 / 3) * (qx - cx);
          const cp1y = cy + (2 / 3) * (qy - cy);
          const cp2x = ex + (2 / 3) * (qx - ex);
          const cp2y = ey + (2 / 3) * (qy - ey);
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = qx; lastCp2Y = qy;
          cx = ex; cy = ey;
        }
        break;
      case 't':
        for (let i = 0; i < nums.length; i += 2) {
          const qx = (lastCmd === 'Q' || lastCmd === 'q' || lastCmd === 'T' || lastCmd === 't')
            ? 2 * cx - lastCp2X : cx;
          const qy = (lastCmd === 'Q' || lastCmd === 'q' || lastCmd === 'T' || lastCmd === 't')
            ? 2 * cy - lastCp2Y : cy;
          const ex = cx + nums[i], ey = cy + nums[i + 1];
          const cp1x = cx + (2 / 3) * (qx - cx);
          const cp1y = cy + (2 / 3) * (qy - cy);
          const cp2x = ex + (2 / 3) * (qx - ex);
          const cp2y = ey + (2 / 3) * (qy - ey);
          if (currentPoints.length > 0) {
            const prev = currentPoints[currentPoints.length - 1];
            prev.handleOut = { x: cp1x - prev.x, y: cp1y - prev.y };
          }
          addPoint(ex, ey, { x: cp2x - ex, y: cp2y - ey }, null);
          lastCp2X = qx; lastCp2Y = qy;
          cx = ex; cy = ey;
        }
        break;

      case 'A':
      case 'a': {
        // Arcs: approximate as line-to-endpoint for V1
        const step = 7;
        for (let i = 0; i < nums.length; i += step) {
          if (cmd === 'A') {
            cx = nums[i + 5]; cy = nums[i + 6];
          } else {
            cx += nums[i + 5]; cy += nums[i + 6];
          }
          addPoint(cx, cy);
        }
        break;
      }

      case 'Z':
      case 'z': {
        // Close the subpath. If the last point overlaps the first, merge them.
        if (currentPoints.length >= 2) {
          const first = currentPoints[0];
          const last = currentPoints[currentPoints.length - 1];
          const dist = Math.hypot(last.x - first.x, last.y - first.y);
          if (dist < 0.5) {
            // Transfer last point's handleIn to first point
            if (last.handleIn) {
              first.handleIn = last.handleIn;
              if (first.handleIn) first.cornerType = 'smooth';
            }
            currentPoints.pop();
          }
        }
        paths.push({ points: currentPoints, closed: true });
        currentPoints = [];
        cx = startX;
        cy = startY;
        break;
      }
    }
    lastCmd = cmd;
  }

  // Flush any remaining unclosed subpath
  if (currentPoints.length > 0) {
    paths.push({ points: currentPoints, closed: false });
  }

  return paths;
}

// ─── SVG Path Serialization ───────────────────────────────────────────────

/**
 * Serialize VectorPath arrays back into an SVG path data string.
 */
export function vectorPathsToSvgPath(paths: VectorPath[]): string {
  const parts: string[] = [];

  for (const vp of paths) {
    if (vp.points.length === 0) continue;

    const pts = vp.points;
    parts.push(`M${fmt(pts[0].x)},${fmt(pts[0].y)}`);

    const len = vp.closed ? pts.length : pts.length - 1;
    for (let i = 0; i < len; i++) {
      const curr = pts[i];
      const next = pts[(i + 1) % pts.length];
      const hasHandleOut = curr.handleOut && (curr.handleOut.x !== 0 || curr.handleOut.y !== 0);
      const hasHandleIn = next.handleIn && (next.handleIn.x !== 0 || next.handleIn.y !== 0);

      if (hasHandleOut || hasHandleIn) {
        // Cubic bezier
        const cp1x = curr.x + (curr.handleOut?.x ?? 0);
        const cp1y = curr.y + (curr.handleOut?.y ?? 0);
        const cp2x = next.x + (next.handleIn?.x ?? 0);
        const cp2y = next.y + (next.handleIn?.y ?? 0);
        parts.push(`C${fmt(cp1x)},${fmt(cp1y)} ${fmt(cp2x)},${fmt(cp2y)} ${fmt(next.x)},${fmt(next.y)}`);
      } else {
        // Straight line
        parts.push(`L${fmt(next.x)},${fmt(next.y)}`);
      }
    }

    if (vp.closed) {
      parts.push('Z');
    }
  }

  return parts.join(' ');
}

function fmt(n: number): string {
  return Math.round(n * 100) / 100 + '';
}

// ─── Hit Testing ──────────────────────────────────────────────────────────

const ANCHOR_HIT_RADIUS = 6;
const HANDLE_HIT_RADIUS = 5;

/**
 * Hit-test a scene-space point against vector points of a node.
 * The vectorPaths are in path-native coordinates; we need the node's
 * pathBounds and node dimensions to map to scene space.
 *
 * @param scenePoint - point in the node's local coordinate space (0..width, 0..height)
 * @param vectorPaths - the vector paths to test
 * @param pathBounds - bounding box of the raw path data
 * @param nodeWidth - the node's width
 * @param nodeHeight - the node's height
 * @param zoom - current viewport zoom
 */
export function hitTestVectorPoint(
  scenePoint: Point,
  vectorPaths: VectorPath[],
  pathBounds: { x: number; y: number; width: number; height: number },
  nodeWidth: number,
  nodeHeight: number,
  zoom: number,
): VectorPointHit | null {
  const scaleX = pathBounds.width > 0 ? nodeWidth / pathBounds.width : 1;
  const scaleY = pathBounds.height > 0 ? nodeHeight / pathBounds.height : 1;

  // Hit radius in path-native space
  const anchorR = ANCHOR_HIT_RADIUS / (zoom * Math.min(scaleX, scaleY));
  const handleR = HANDLE_HIT_RADIUS / (zoom * Math.min(scaleX, scaleY));

  // Convert scene point to path-native space
  const pathX = scenePoint.x / scaleX + pathBounds.x;
  const pathY = scenePoint.y / scaleY + pathBounds.y;

  // Check handles first (they are on top visually), then anchors
  for (let pi = 0; pi < vectorPaths.length; pi++) {
    const pts = vectorPaths[pi].points;
    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i];

      // handleOut
      if (pt.handleOut && (pt.handleOut.x !== 0 || pt.handleOut.y !== 0)) {
        const hx = pt.x + pt.handleOut.x;
        const hy = pt.y + pt.handleOut.y;
        if (Math.hypot(pathX - hx, pathY - hy) <= handleR) {
          return { pathIndex: pi, pointIndex: i, part: 'handleOut' };
        }
      }

      // handleIn
      if (pt.handleIn && (pt.handleIn.x !== 0 || pt.handleIn.y !== 0)) {
        const hx = pt.x + pt.handleIn.x;
        const hy = pt.y + pt.handleIn.y;
        if (Math.hypot(pathX - hx, pathY - hy) <= handleR) {
          return { pathIndex: pi, pointIndex: i, part: 'handleIn' };
        }
      }
    }
  }

  // Then check anchors
  for (let pi = 0; pi < vectorPaths.length; pi++) {
    const pts = vectorPaths[pi].points;
    for (let i = 0; i < pts.length; i++) {
      if (Math.hypot(pathX - pts[i].x, pathY - pts[i].y) <= anchorR) {
        return { pathIndex: pi, pointIndex: i, part: 'anchor' };
      }
    }
  }

  return null;
}

// ─── Shape-to-Path Converters ─────────────────────────────────────────────

/** Kappa constant for cubic bezier approximation of quarter-circle arcs */
const KAPPA = 0.5522847498;

/**
 * Convert a rectangle (with optional corner radius) to vector paths.
 * Coordinates are in 0..width, 0..height space.
 */
export function rectToVectorPaths(
  width: number,
  height: number,
  cornerRadius?: number,
  cornerRadii?: number[],
): VectorPath[] {
  const r = cornerRadii
    ? cornerRadii.map(cr => Math.min(cr, width / 2, height / 2))
    : Array(4).fill(Math.min(cornerRadius ?? 0, width / 2, height / 2));

  const [tl, tr, br, bl] = r;

  if (tl === 0 && tr === 0 && br === 0 && bl === 0) {
    // Simple rectangle: 4 corner points
    return [{
      points: [
        { x: 0, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' },
        { x: width, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' },
        { x: width, y: height, handleIn: null, handleOut: null, cornerType: 'corner' },
        { x: 0, y: height, handleIn: null, handleOut: null, cornerType: 'corner' },
      ],
      closed: true,
    }];
  }

  // Rounded rectangle: each corner becomes a smooth point with bezier handles
  const points: VectorPoint[] = [];

  // Top-left corner
  if (tl > 0) {
    points.push({ x: 0, y: tl, handleIn: null, handleOut: { x: 0, y: -tl * KAPPA }, cornerType: 'smooth' });
    points.push({ x: tl, y: 0, handleIn: { x: -tl * KAPPA, y: 0 }, handleOut: null, cornerType: 'smooth' });
  } else {
    points.push({ x: 0, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' });
  }

  // Top-right corner
  if (tr > 0) {
    points.push({ x: width - tr, y: 0, handleIn: null, handleOut: { x: tr * KAPPA, y: 0 }, cornerType: 'smooth' });
    points.push({ x: width, y: tr, handleIn: { x: 0, y: -tr * KAPPA }, handleOut: null, cornerType: 'smooth' });
  } else {
    points.push({ x: width, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' });
  }

  // Bottom-right corner
  if (br > 0) {
    points.push({ x: width, y: height - br, handleIn: null, handleOut: { x: 0, y: br * KAPPA }, cornerType: 'smooth' });
    points.push({ x: width - br, y: height, handleIn: { x: br * KAPPA, y: 0 }, handleOut: null, cornerType: 'smooth' });
  } else {
    points.push({ x: width, y: height, handleIn: null, handleOut: null, cornerType: 'corner' });
  }

  // Bottom-left corner
  if (bl > 0) {
    points.push({ x: bl, y: height, handleIn: null, handleOut: { x: -bl * KAPPA, y: 0 }, cornerType: 'smooth' });
    points.push({ x: 0, y: height - bl, handleIn: { x: 0, y: bl * KAPPA }, handleOut: null, cornerType: 'smooth' });
  } else {
    points.push({ x: 0, y: height, handleIn: null, handleOut: null, cornerType: 'corner' });
  }

  return [{ points, closed: true }];
}

/**
 * Convert an ellipse to vector paths using 4-point cubic bezier approximation.
 * Coordinates are in 0..width, 0..height space.
 */
export function ellipseToVectorPaths(width: number, height: number): VectorPath[] {
  const rx = width / 2;
  const ry = height / 2;
  const cx = rx;
  const cy = ry;
  const kx = rx * KAPPA;
  const ky = ry * KAPPA;

  return [{
    points: [
      // Top
      { x: cx, y: 0, handleIn: { x: kx, y: 0 }, handleOut: { x: -kx, y: 0 }, cornerType: 'smooth' },
      // Left
      { x: 0, y: cy, handleIn: { x: 0, y: -ky }, handleOut: { x: 0, y: ky }, cornerType: 'smooth' },
      // Bottom
      { x: cx, y: height, handleIn: { x: -kx, y: 0 }, handleOut: { x: kx, y: 0 }, cornerType: 'smooth' },
      // Right
      { x: width, y: cy, handleIn: { x: 0, y: ky }, handleOut: { x: 0, y: -ky }, cornerType: 'smooth' },
    ],
    closed: true,
  }];
}

/**
 * Convert a regular polygon to vector paths.
 */
export function polygonToVectorPaths(width: number, height: number, sides: number): VectorPath[] {
  const cx = width / 2;
  const cy = height / 2;
  const rx = width / 2;
  const ry = height / 2;
  const points: VectorPoint[] = [];

  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
    points.push({
      x: cx + rx * Math.cos(angle),
      y: cy + ry * Math.sin(angle),
      handleIn: null,
      handleOut: null,
      cornerType: 'corner',
    });
  }

  return [{ points, closed: true }];
}

/**
 * Convert a seal/sticker to vector paths. Anchor points reproduce the
 * renderer's geometry exactly: 'sharp' alternates outer/inner anchors;
 * 'wavy' samples the cosine boundary at the same 8-per-bump density.
 */
export function sealToVectorPaths(
  width: number,
  height: number,
  bumps: number,
  innerRatio: number,
  style: 'sharp' | 'wavy',
): VectorPath[] {
  const cx = width / 2;
  const cy = height / 2;
  const rxOuter = width / 2;
  const ryOuter = height / 2;
  const ratio = innerRatio <= 1 ? innerRatio : innerRatio / Math.max(rxOuter, ryOuter);
  const rxInner = rxOuter * ratio;
  const ryInner = ryOuter * ratio;
  const points: VectorPoint[] = [];
  const safeBumps = Math.max(3, Math.floor(bumps));
  if (style === 'sharp') {
    const total = safeBumps * 2;
    for (let i = 0; i < total; i++) {
      const isOuter = i % 2 === 0;
      const rx = isOuter ? rxOuter : rxInner;
      const ry = isOuter ? ryOuter : ryInner;
      const a = (i * Math.PI) / safeBumps - Math.PI / 2;
      points.push({
        x: cx + rx * Math.cos(a),
        y: cy + ry * Math.sin(a),
        handleIn: null,
        handleOut: null,
        cornerType: 'corner',
      });
    }
  } else {
    const samples = safeBumps * 8;
    for (let i = 0; i < samples; i++) {
      const t = (i / samples) * Math.PI * 2;
      const w = (1 + Math.cos(safeBumps * t - Math.PI)) / 2;
      const rx = rxInner + (rxOuter - rxInner) * (1 - w);
      const ry = ryInner + (ryOuter - ryInner) * (1 - w);
      const a = t - Math.PI / 2;
      points.push({
        x: cx + rx * Math.cos(a),
        y: cy + ry * Math.sin(a),
        handleIn: null,
        handleOut: null,
        cornerType: 'corner',
      });
    }
  }
  return [{ points, closed: true }];
}

/**
 * Convert a trapezoid to vector paths.
 */
export function trapezoidToVectorPaths(width: number, height: number, skew: number): VectorPath[] {
  const s = Math.max(-width, Math.min(width, skew));
  const topLeftX = s < 0 ? -s : 0;
  const topRightX = s < 0 ? width : width - s;
  const points: VectorPoint[] = [
    { x: topLeftX, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' },
    { x: topRightX, y: 0, handleIn: null, handleOut: null, cornerType: 'corner' },
    { x: width, y: height, handleIn: null, handleOut: null, cornerType: 'corner' },
    { x: 0, y: height, handleIn: null, handleOut: null, cornerType: 'corner' },
  ];
  return [{ points, closed: true }];
}

/**
 * Convert a star to vector paths.
 */
export function starToVectorPaths(
  width: number,
  height: number,
  pointCount: number,
  innerRadius: number,
): VectorPath[] {
  const cx = width / 2;
  const cy = height / 2;
  const outerRx = width / 2;
  const outerRy = height / 2;
  const innerRx = outerRx * innerRadius;
  const innerRy = outerRy * innerRadius;
  const points: VectorPoint[] = [];
  const totalPoints = pointCount * 2;

  for (let i = 0; i < totalPoints; i++) {
    const angle = (i / totalPoints) * Math.PI * 2 - Math.PI / 2;
    const isOuter = i % 2 === 0;
    const rx = isOuter ? outerRx : innerRx;
    const ry = isOuter ? outerRy : innerRy;
    points.push({
      x: cx + rx * Math.cos(angle),
      y: cy + ry * Math.sin(angle),
      handleIn: null,
      handleOut: null,
      cornerType: 'corner',
    });
  }

  return [{ points, closed: true }];
}

// ─── Edge Hit Testing & Point Insertion ───────────────────────────────────

export interface EdgeHit {
  pathIndex: number;
  segmentIndex: number;
  /** Parameter along the segment [0, 1] */
  t: number;
}

/**
 * Find the nearest edge (segment) to a point in path-native coordinates.
 * Returns the segment info + parameter t if within threshold, or null.
 */
export function hitTestEdge(
  scenePoint: Point,
  vectorPaths: VectorPath[],
  pathBounds: { x: number; y: number; width: number; height: number },
  nodeWidth: number,
  nodeHeight: number,
  zoom: number,
): EdgeHit | null {
  const scaleX = pathBounds.width > 0 ? nodeWidth / pathBounds.width : 1;
  const scaleY = pathBounds.height > 0 ? nodeHeight / pathBounds.height : 1;
  const threshold = 8 / (zoom * Math.min(scaleX, scaleY));

  const px = scenePoint.x / scaleX + pathBounds.x;
  const py = scenePoint.y / scaleY + pathBounds.y;

  let best: EdgeHit | null = null;
  let bestDist = threshold;

  for (let pi = 0; pi < vectorPaths.length; pi++) {
    const pts = vectorPaths[pi].points;
    const segCount = vectorPaths[pi].closed ? pts.length : pts.length - 1;

    for (let si = 0; si < segCount; si++) {
      const curr = pts[si];
      const next = pts[(si + 1) % pts.length];
      const hasOut = curr.handleOut && (curr.handleOut.x !== 0 || curr.handleOut.y !== 0);
      const hasIn = next.handleIn && (next.handleIn.x !== 0 || next.handleIn.y !== 0);

      let dist: number;
      let t: number;

      if (hasOut || hasIn) {
        // Cubic bezier: sample and find nearest
        const result = nearestOnCubic(
          px, py,
          curr.x, curr.y,
          curr.x + (curr.handleOut?.x ?? 0), curr.y + (curr.handleOut?.y ?? 0),
          next.x + (next.handleIn?.x ?? 0), next.y + (next.handleIn?.y ?? 0),
          next.x, next.y,
        );
        dist = result.dist;
        t = result.t;
      } else {
        // Straight line
        const result = nearestOnLine(px, py, curr.x, curr.y, next.x, next.y);
        dist = result.dist;
        t = result.t;
      }

      if (dist < bestDist) {
        bestDist = dist;
        best = { pathIndex: pi, segmentIndex: si, t };
      }
    }
  }

  return best;
}

function nearestOnLine(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { dist: Math.hypot(px - ax, py - ay), t: 0 };
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const nx = ax + t * dx, ny = ay + t * dy;
  return { dist: Math.hypot(px - nx, py - ny), t };
}

function nearestOnCubic(
  px: number, py: number,
  x0: number, y0: number, x1: number, y1: number,
  x2: number, y2: number, x3: number, y3: number,
) {
  // Sample 20 points along the curve
  let bestDist = Infinity;
  let bestT = 0;
  const steps = 20;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const mt = 1 - t;
    const x = mt * mt * mt * x0 + 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t * x3;
    const y = mt * mt * mt * y0 + 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t * y3;
    const d = Math.hypot(px - x, py - y);
    if (d < bestDist) { bestDist = d; bestT = t; }
  }
  return { dist: bestDist, t: bestT };
}

/**
 * Split a segment at parameter t and insert a new point.
 * Returns the modified VectorPath with the new point inserted.
 */
export function insertPointOnSegment(
  path: VectorPath,
  segmentIndex: number,
  t: number,
): VectorPath {
  const pts = path.points;
  const curr = pts[segmentIndex];
  const nextIdx = (segmentIndex + 1) % pts.length;
  const next = pts[nextIdx];

  const hasOut = curr.handleOut && (curr.handleOut.x !== 0 || curr.handleOut.y !== 0);
  const hasIn = next.handleIn && (next.handleIn.x !== 0 || next.handleIn.y !== 0);

  let newPoint: VectorPoint;
  let newCurrHandleOut: VectorPoint['handleOut'] = null;
  let newNextHandleIn: VectorPoint['handleIn'] = null;

  if (hasOut || hasIn) {
    // De Casteljau subdivision of cubic bezier at t
    const p0x = curr.x, p0y = curr.y;
    const p1x = curr.x + (curr.handleOut?.x ?? 0), p1y = curr.y + (curr.handleOut?.y ?? 0);
    const p2x = next.x + (next.handleIn?.x ?? 0), p2y = next.y + (next.handleIn?.y ?? 0);
    const p3x = next.x, p3y = next.y;

    // Level 1
    const a1x = lerp(p0x, p1x, t), a1y = lerp(p0y, p1y, t);
    const a2x = lerp(p1x, p2x, t), a2y = lerp(p1y, p2y, t);
    const a3x = lerp(p2x, p3x, t), a3y = lerp(p2y, p3y, t);
    // Level 2
    const b1x = lerp(a1x, a2x, t), b1y = lerp(a1y, a2y, t);
    const b2x = lerp(a2x, a3x, t), b2y = lerp(a2y, a3y, t);
    // Level 3 (the split point)
    const cx = lerp(b1x, b2x, t), cy = lerp(b1y, b2y, t);

    newCurrHandleOut = { x: a1x - p0x, y: a1y - p0y };
    newNextHandleIn = { x: a3x - p3x, y: a3y - p3y };
    newPoint = {
      x: cx, y: cy,
      handleIn: { x: b1x - cx, y: b1y - cy },
      handleOut: { x: b2x - cx, y: b2y - cy },
      cornerType: 'smooth',
    };
  } else {
    // Straight line: just lerp
    const nx = lerp(curr.x, next.x, t);
    const ny = lerp(curr.y, next.y, t);
    newPoint = { x: nx, y: ny, handleIn: null, handleOut: null, cornerType: 'corner' };
  }

  // Build new points array
  const newPoints = [...pts];
  // Update current point's handleOut
  newPoints[segmentIndex] = { ...newPoints[segmentIndex], handleOut: newCurrHandleOut };
  // Update next point's handleIn
  newPoints[nextIdx] = { ...newPoints[nextIdx], handleIn: newNextHandleIn };
  // Insert new point after segmentIndex
  const insertAt = segmentIndex + 1;
  newPoints.splice(insertAt, 0, newPoint);

  return { ...path, points: newPoints };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ─── Snap-to-Grid ─────────────────────────────────────────────────────────

/**
 * Snap a value to the nearest grid increment.
 */
export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap a VectorPoint's anchor to a grid.
 */
export function snapPointToGrid(pt: VectorPoint, gridSize: number): VectorPoint {
  return {
    ...pt,
    x: snapToGrid(pt.x, gridSize),
    y: snapToGrid(pt.y, gridSize),
  };
}

// ─── Path Simplification (Ramer-Douglas-Peucker) ──────────────────────────

/**
 * Simplify a VectorPath by removing points that don't contribute significantly
 * to the shape. Uses the Ramer-Douglas-Peucker algorithm on anchor positions.
 * Only removes corner points (preserves smooth/bezier points).
 */
export function simplifyPath(path: VectorPath, tolerance: number): VectorPath {
  if (path.points.length <= 2) return path;

  const pts = path.points;

  if (path.closed) {
    // For closed paths, find the point farthest from centroid as the fixed start
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    let maxDist = 0, startIdx = 0;
    for (let i = 0; i < pts.length; i++) {
      const d = Math.hypot(pts[i].x - cx, pts[i].y - cy);
      if (d > maxDist) { maxDist = d; startIdx = i; }
    }
    // Rotate array so the farthest point is first
    const rotated = [...pts.slice(startIdx), ...pts.slice(0, startIdx)];
    const simplified = rdpSimplify(rotated, tolerance);
    return { ...path, points: simplified.length >= 3 ? simplified : pts };
  }

  const simplified = rdpSimplify(pts, tolerance);
  return { ...path, points: simplified.length >= 2 ? simplified : pts };
}

function rdpSimplify(points: VectorPoint[], tolerance: number): VectorPoint[] {
  if (points.length <= 2) return points;

  // Find point with max distance from line between first and last
  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    // Skip points with bezier handles -- they're important for curves
    const pt = points[i];
    if (pt.handleIn && (pt.handleIn.x !== 0 || pt.handleIn.y !== 0)) continue;
    if (pt.handleOut && (pt.handleOut.x !== 0 || pt.handleOut.y !== 0)) continue;

    const d = pointToLineDistance(pt.x, pt.y, first.x, first.y, last.x, last.y);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }

  if (maxDist > tolerance) {
    const left = rdpSimplify(points.slice(0, maxIdx + 1), tolerance);
    const right = rdpSimplify(points.slice(maxIdx), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  // All points within tolerance -- keep only endpoints and any with handles
  const result = [first];
  for (let i = 1; i < points.length - 1; i++) {
    const pt = points[i];
    const hasHandle = (pt.handleIn && (pt.handleIn.x !== 0 || pt.handleIn.y !== 0))
      || (pt.handleOut && (pt.handleOut.x !== 0 || pt.handleOut.y !== 0));
    if (hasHandle) result.push(pt);
  }
  result.push(last);
  return result;
}

function pointToLineDistance(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

// ─── Freehand Path Fitting ────────────────────────────────────────────────

/**
 * Convert raw mouse points into a smooth VectorPath with bezier curves.
 * Uses a simple algorithm: subsample, then fit cubic beziers between key points.
 */
export function fitFreehandPath(rawPoints: Point[], smoothing: number = 0.3): VectorPath {
  if (rawPoints.length < 2) {
    return {
      points: rawPoints.map(p => ({
        x: p.x, y: p.y, handleIn: null, handleOut: null, cornerType: 'corner' as const,
      })),
      closed: false,
    };
  }

  // 1. Remove duplicate consecutive points
  const deduped: Point[] = [rawPoints[0]];
  for (let i = 1; i < rawPoints.length; i++) {
    const prev = deduped[deduped.length - 1];
    if (Math.hypot(rawPoints[i].x - prev.x, rawPoints[i].y - prev.y) > 2) {
      deduped.push(rawPoints[i]);
    }
  }
  if (deduped.length < 2) {
    return { points: [{ x: deduped[0].x, y: deduped[0].y, handleIn: null, handleOut: null, cornerType: 'corner' }], closed: false };
  }

  // 2. Subsample to reduce point count (keep every Nth point based on path length)
  const totalLen = deduped.reduce((sum, p, i) =>
    i > 0 ? sum + Math.hypot(p.x - deduped[i - 1].x, p.y - deduped[i - 1].y) : 0, 0);
  const targetCount = Math.max(4, Math.min(50, Math.ceil(totalLen / 20)));
  const step = Math.max(1, Math.floor(deduped.length / targetCount));
  const sampled: Point[] = [];
  for (let i = 0; i < deduped.length; i += step) {
    sampled.push(deduped[i]);
  }
  // Always include the last point
  if (sampled[sampled.length - 1] !== deduped[deduped.length - 1]) {
    sampled.push(deduped[deduped.length - 1]);
  }

  // 3. Generate smooth VectorPoints with handles
  const points: VectorPoint[] = sampled.map((p, i) => {
    if (i === 0 || i === sampled.length - 1) {
      // Endpoints: handle pointing toward neighbor
      const neighbor = i === 0 ? sampled[1] : sampled[sampled.length - 2];
      const dx = neighbor.x - p.x;
      const dy = neighbor.y - p.y;
      const len = Math.hypot(dx, dy) * smoothing;
      if (len < 1) return { x: p.x, y: p.y, handleIn: null, handleOut: null, cornerType: 'corner' as const };

      const ux = dx / Math.hypot(dx, dy);
      const uy = dy / Math.hypot(dx, dy);
      if (i === 0) {
        return { x: p.x, y: p.y, handleIn: null, handleOut: { x: ux * len, y: uy * len }, cornerType: 'smooth' as const };
      } else {
        return { x: p.x, y: p.y, handleIn: { x: -ux * len, y: -uy * len }, handleOut: null, cornerType: 'smooth' as const };
      }
    }

    // Interior points: handles based on direction between prev and next
    const prev = sampled[i - 1];
    const next = sampled[i + 1];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return { x: p.x, y: p.y, handleIn: null, handleOut: null, cornerType: 'corner' as const };

    const ux = dx / dist;
    const uy = dy / dist;
    const prevDist = Math.hypot(p.x - prev.x, p.y - prev.y) * smoothing;
    const nextDist = Math.hypot(next.x - p.x, next.y - p.y) * smoothing;

    return {
      x: p.x, y: p.y,
      handleIn: { x: -ux * prevDist, y: -uy * prevDist },
      handleOut: { x: ux * nextDist, y: uy * nextDist },
      cornerType: 'smooth' as const,
    };
  });

  return { points, closed: false };
}

// ─── SVG Import ───────────────────────────────────────────────────────────

/**
 * Parse a complete SVG string and extract all path elements as VectorPaths.
 * Also extracts basic shapes (rect, circle, ellipse, line, polygon, polyline).
 */
export function parseSvgToVectorPaths(svgString: string): {
  paths: VectorPath[];
  viewBox: { x: number; y: number; width: number; height: number } | null;
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svg = doc.querySelector('svg');

  let viewBox: { x: number; y: number; width: number; height: number } | null = null;
  if (svg) {
    const vb = svg.getAttribute('viewBox');
    if (vb) {
      const parts = vb.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        viewBox = { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
      }
    } else {
      const w = parseFloat(svg.getAttribute('width') || '0');
      const h = parseFloat(svg.getAttribute('height') || '0');
      if (w > 0 && h > 0) viewBox = { x: 0, y: 0, width: w, height: h };
    }
  }

  const allPaths: VectorPath[] = [];

  // Extract <path> elements
  const pathEls = doc.querySelectorAll('path');
  for (const el of pathEls) {
    const d = el.getAttribute('d');
    if (d) {
      allPaths.push(...parseSvgPathToVectorPaths(d));
    }
  }

  // Extract <rect> elements
  const rectEls = doc.querySelectorAll('rect');
  for (const el of rectEls) {
    const x = parseFloat(el.getAttribute('x') || '0');
    const y = parseFloat(el.getAttribute('y') || '0');
    const w = parseFloat(el.getAttribute('width') || '0');
    const h = parseFloat(el.getAttribute('height') || '0');
    const rx = parseFloat(el.getAttribute('rx') || '0');
    if (w > 0 && h > 0) {
      const rp = rectToVectorPaths(w, h, rx);
      // Offset to the rect's position
      for (const vp of rp) {
        for (const pt of vp.points) { pt.x += x; pt.y += y; }
      }
      allPaths.push(...rp);
    }
  }

  // Extract <circle> elements
  const circleEls = doc.querySelectorAll('circle');
  for (const el of circleEls) {
    const cx = parseFloat(el.getAttribute('cx') || '0');
    const cy = parseFloat(el.getAttribute('cy') || '0');
    const r = parseFloat(el.getAttribute('r') || '0');
    if (r > 0) {
      const ep = ellipseToVectorPaths(r * 2, r * 2);
      for (const vp of ep) {
        for (const pt of vp.points) { pt.x += cx - r; pt.y += cy - r; }
      }
      allPaths.push(...ep);
    }
  }

  // Extract <ellipse> elements
  const ellipseEls = doc.querySelectorAll('ellipse');
  for (const el of ellipseEls) {
    const cx = parseFloat(el.getAttribute('cx') || '0');
    const cy = parseFloat(el.getAttribute('cy') || '0');
    const rx = parseFloat(el.getAttribute('rx') || '0');
    const ry = parseFloat(el.getAttribute('ry') || '0');
    if (rx > 0 && ry > 0) {
      const ep = ellipseToVectorPaths(rx * 2, ry * 2);
      for (const vp of ep) {
        for (const pt of vp.points) { pt.x += cx - rx; pt.y += cy - ry; }
      }
      allPaths.push(...ep);
    }
  }

  // Extract <polygon> and <polyline> elements
  for (const tag of ['polygon', 'polyline'] as const) {
    const els = doc.querySelectorAll(tag);
    for (const el of els) {
      const pointsAttr = el.getAttribute('points');
      if (!pointsAttr) continue;
      const nums = pointsAttr.trim().split(/[\s,]+/).map(Number);
      const pts: VectorPoint[] = [];
      for (let i = 0; i < nums.length - 1; i += 2) {
        pts.push({ x: nums[i], y: nums[i + 1], handleIn: null, handleOut: null, cornerType: 'corner' });
      }
      if (pts.length >= 2) {
        allPaths.push({ points: pts, closed: tag === 'polygon' });
      }
    }
  }

  // Extract <line> elements
  const lineEls = doc.querySelectorAll('line');
  for (const el of lineEls) {
    const x1 = parseFloat(el.getAttribute('x1') || '0');
    const y1 = parseFloat(el.getAttribute('y1') || '0');
    const x2 = parseFloat(el.getAttribute('x2') || '0');
    const y2 = parseFloat(el.getAttribute('y2') || '0');
    allPaths.push({
      points: [
        { x: x1, y: y1, handleIn: null, handleOut: null, cornerType: 'corner' },
        { x: x2, y: y2, handleIn: null, handleOut: null, cornerType: 'corner' },
      ],
      closed: false,
    });
  }

  return { paths: allPaths, viewBox };
}

// ─── Path Bounds ──────────────────────────────────────────────────────────

/**
 * Compute the bounding box of a set of vector paths (from control points only).
 */
export function getVectorPathsBounds(paths: VectorPath[]): { x: number; y: number; width: number; height: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const vp of paths) {
    for (const pt of vp.points) {
      if (pt.x < minX) minX = pt.x;
      if (pt.x > maxX) maxX = pt.x;
      if (pt.y < minY) minY = pt.y;
      if (pt.y > maxY) maxY = pt.y;

      if (pt.handleIn) {
        const hx = pt.x + pt.handleIn.x, hy = pt.y + pt.handleIn.y;
        if (hx < minX) minX = hx; if (hx > maxX) maxX = hx;
        if (hy < minY) minY = hy; if (hy > maxY) maxY = hy;
      }
      if (pt.handleOut) {
        const hx = pt.x + pt.handleOut.x, hy = pt.y + pt.handleOut.y;
        if (hx < minX) minX = hx; if (hx > maxX) maxX = hx;
        if (hy < minY) minY = hy; if (hy > maxY) maxY = hy;
      }
    }
  }

  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
